import React, { useEffect, useState } from 'react';
import { FiX, FiCopy, FiCheck } from 'react-icons/fi';
import './ShareSettingsModal.css';

const DEFAULT_PERMS = {
  canView: true,
  canAddItems: false,
  canEditItems: false,
  canCompleteItems: false,
  canComment: false,
  canUploadFiles: false,
  canSeeTotals: true,
};

const ShareSettingsModal = ({ isOpen, onClose, list, token, onSaved }) => {
  const [enabled, setEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [perms, setPerms] = useState(DEFAULT_PERMS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (list) {
      setEnabled(!!list.externalShareEnabled);
      setPerms({ ...DEFAULT_PERMS, ...(list.externalPermissions || {}) });
      setPassword('');
      setClearPassword(false);
      setError('');
    }
  }, [list, isOpen]);

  if (!isOpen || !list) return null;

  const publicUrl = list.externalToken
    ? `${window.location.origin}/share/${list.externalToken}`
    : '';

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) {}
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        externalShareEnabled: enabled,
        externalPermissions: perms,
      };
      if (clearPassword) {
        payload.externalPassword = '';
      } else if (password) {
        payload.externalPassword = password;
      }
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shared-lists/${list.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      const d = await r.json();
      onSaved && onSaved(d.sharedList);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePerm = (key) => setPerms({ ...perms, [key]: !perms[key] });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-settings-modal" onClick={(e) => e.stopPropagation()} data-testid="share-settings-modal">
        <div className="modal-header">
          <h2>Share Externally</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="share-toggle-row">
            <label className="share-switch">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                data-testid="share-toggle"
              />
              <span className="share-switch-track" />
              <span className="share-switch-thumb" />
            </label>
            <div>
              <div className="share-toggle-title">Enable public link</div>
              <div className="share-toggle-hint">
                Anyone with the link can access this list, optionally protected by a password.
              </div>
            </div>
          </div>

          {enabled && publicUrl && (
            <div className="share-link-row">
              <input className="input" value={publicUrl} readOnly data-testid="share-url-input" />
              <button className="btn btn-secondary btn-sm" onClick={copyLink} data-testid="share-copy-button">
                {copied ? <><FiCheck size={14} /> Copied</> : <><FiCopy size={14} /> Copy</>}
              </button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Password (optional)</label>
            <input
              type="text"
              className="input"
              placeholder={list.externalPasswordHash ? '•••••••• (already set — type to change)' : 'Leave empty for no password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setClearPassword(false); }}
              disabled={clearPassword}
              data-testid="share-password-input"
            />
            {list.externalPasswordHash && (
              <label className="share-clear-pw">
                <input
                  type="checkbox"
                  checked={clearPassword}
                  onChange={(e) => { setClearPassword(e.target.checked); if (e.target.checked) setPassword(''); }}
                  data-testid="share-clear-password"
                />
                <span>Remove existing password</span>
              </label>
            )}
          </div>

          <div className="share-perms">
            <div className="share-perms-title">External permissions</div>
            {[
              { key: 'canView', label: 'Can view' },
              { key: 'canSeeTotals', label: 'Can see prices / totals (uncheck to hide money)' },
              { key: 'canAddItems', label: 'Can add items' },
              { key: 'canEditItems', label: 'Can edit items' },
              { key: 'canCompleteItems', label: 'Can complete items' },
              { key: 'canComment', label: 'Can comment' },
              { key: 'canUploadFiles', label: 'Can upload files (coming soon)', disabled: true },
            ].map((p) => (
              <label key={p.key} className={`share-perm-row ${p.disabled ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!perms[p.key]}
                  onChange={() => !p.disabled && togglePerm(p.key)}
                  disabled={p.disabled}
                  data-testid={`share-perm-${p.key}`}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>

          {error && <div className="form-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="share-save-button">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSettingsModal;
